import React from "react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, Cell, ResponsiveContainer, Legend, LabelList,
} from "recharts";
import "../DashboardStyles.css";

// ─── Data ────────────────────────────────────────────────────────────────────
// BEA Regional Economic Accounts, Table CAINC1 (county data Feb 2, 2026
// revision; state data Apr 9, 2026 revision). Regional and district figures are
// population-weighted: total personal income ÷ total population.

const PANHANDLE_2024 = 60921;
const NEBRASKA_2024 = 72701;

const countyPcpi2024 = [
  { county: "Sioux",        pcpi: 85546 },
  { county: "Morrill",      pcpi: 79808 },
  { county: "Banner",       pcpi: 69174 },
  { county: "Sheridan",     pcpi: 69010 },
  { county: "Kimball",      pcpi: 64056 },
  { county: "Garden",       pcpi: 62150 },
  { county: "Box Butte",    pcpi: 60772 },
  { county: "Scotts Bluff", pcpi: 60200 },
  { county: "Cheyenne",     pcpi: 55224 },
  { county: "Dawes",        pcpi: 53452 },
  { county: "Deuel",        pcpi: 46222 },
];

const panhandleVsState = [
  { year: "2020", nebraska: 56909, panhandle: 51157 },
  { year: "2021", nebraska: 63658, panhandle: 55329 },
  { year: "2022", nebraska: 66365, panhandle: 54973 },
  { year: "2023", nebraska: 71517, panhandle: 59504 },
  { year: "2024", nebraska: 72701, panhandle: 60921 },
];

const districts = [
  { year: "2021", MAPA: 67586, CNEDD: 65522, SCEDD: 64106, NENEDD: 61517, WCNDD: 60840, SENDD: 60073, PADD: 55329 },
  { year: "2022", MAPA: 72807, CNEDD: 61774, SCEDD: 62475, NENEDD: 62472, WCNDD: 61941, SENDD: 62181, PADD: 54973 },
  { year: "2023", MAPA: 78280, CNEDD: 70644, SCEDD: 68684, NENEDD: 67558, WCNDD: 66567, SENDD: 66152, PADD: 59504 },
  { year: "2024", MAPA: 80711, CNEDD: 67435, SCEDD: 68424, NENEDD: 65644, WCNDD: 64543, SENDD: 67999, PADD: 60921 },
];
const OTHER_DISTRICTS = ["MAPA", "CNEDD", "SCEDD", "SENDD", "NENEDD", "WCNDD"];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const NAVY = "#1B2A4A";
const SAGE = "#7A8B6F";
const RUST = "#A8512A";
const GREY = "#9AA3AD";

const fmtK = (v) => `$${Math.round(v / 1000)}k`;
const fmt$ = (v) => `$${Number(v).toLocaleString()}`;

// ─── Figures ─────────────────────────────────────────────────────────────────

export function Figure23a() {
  return (
    <figure className="ceds-figure">
      <p className="fig-title">Figure 2.3a — Per capita personal income by county, 2024</p>
      <ResponsiveContainer width="100%" height={380}>
        <BarChart data={countyPcpi2024} layout="vertical" margin={{ top: 26, right: 64, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E7E2D6" />
          <XAxis type="number" tickFormatter={fmtK} domain={[0, 90000]} tick={{ fontSize: 12 }} />
          <YAxis type="category" dataKey="county" width={92} tick={{ fontSize: 12 }} />
          <Tooltip formatter={(v) => [fmt$(v), "Per capita personal income"]} />
          <ReferenceLine x={PANHANDLE_2024} stroke="#555" strokeDasharray="2 3"
            label={{ value: `Panhandle ${fmt$(PANHANDLE_2024)}`, position: "top", fontSize: 11 }} />
          <ReferenceLine x={NEBRASKA_2024} stroke={RUST} strokeDasharray="6 4"
            label={{ value: `Nebraska ${fmt$(NEBRASKA_2024)}`, position: "top", fill: RUST, fontSize: 11 }} />
          <Bar dataKey="pcpi" isAnimationActive={false}>
            {countyPcpi2024.map((d) => (
              <Cell key={d.county} fill={d.pcpi >= NEBRASKA_2024 ? SAGE : NAVY} />
            ))}
            <LabelList dataKey="pcpi" position="right" formatter={fmt$} style={{ fontSize: 11 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <figcaption>
        Figure 2.3a. Per capita personal income by county, 2024. Sioux and Morrill
        counties exceed the Nebraska average; both are among the region’s least
        populated. Source: U.S. Bureau of Economic Analysis, Regional Economic
        Accounts (CAINC1).
      </figcaption>
    </figure>
  );
}

export function Figure23b() {
  const data = panhandleVsState.map((d) => ({
    ...d,
    gap: d.nebraska - d.panhandle,
    gapPct: (((d.nebraska - d.panhandle) / d.nebraska) * 100).toFixed(1),
  }));
  return (
    <figure className="ceds-figure">
      <p className="fig-title">Figure 2.3b — Panhandle vs. Nebraska per capita personal income, 2020–2024</p>
      <ResponsiveContainer width="100%" height={340}>
        <LineChart data={data} margin={{ top: 20, right: 32, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E7E2D6" />
          <XAxis dataKey="year" tick={{ fontSize: 12 }} />
          <YAxis tickFormatter={fmtK} domain={[48000, 76000]} tick={{ fontSize: 12 }} />
          <Tooltip
            formatter={(v, name) => [fmt$(v), name]}
            labelFormatter={(y) => {
              const d = data.find((r) => r.year === y);
              return `${y} — gap ${fmt$(d.gap)} (${d.gapPct}%)`;
            }}
          />
          <Legend />
          <Line type="monotone" dataKey="nebraska" name="Nebraska" stroke={NAVY} strokeWidth={2.5} dot={{ r: 4 }} isAnimationActive={false}>
            <LabelList dataKey="nebraska" position="top" formatter={fmt$} style={{ fontSize: 11, fill: NAVY }} />
          </Line>
          <Line type="monotone" dataKey="panhandle" name="Panhandle (population-weighted)" stroke={SAGE} strokeWidth={2.5} dot={{ r: 4 }} isAnimationActive={false}>
            <LabelList dataKey="panhandle" position="bottom" formatter={fmt$} style={{ fontSize: 11, fill: "#56654C" }} />
          </Line>
        </LineChart>
      </ResponsiveContainer>
      <figcaption>
        Figure 2.3b. Panhandle population-weighted vs. Nebraska statewide per
        capita personal income, 2020–2024. Regional figures calculated as total
        regional personal income divided by total regional population. Source:
        U.S. Bureau of Economic Analysis, Regional Economic Accounts (CAINC1).
      </figcaption>
    </figure>
  );
}

export function Figure23c() {
  return (
    <figure className="ceds-figure">
      <p className="fig-title">Figure 2.3c — Per capita personal income, Nebraska development districts, 2021–2024</p>
      <ResponsiveContainer width="100%" height={360}>
        <LineChart data={districts} margin={{ top: 12, right: 64, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E7E2D6" />
          <XAxis dataKey="year" tick={{ fontSize: 12 }} />
          <YAxis tickFormatter={fmtK} domain={[50000, 82000]} tick={{ fontSize: 12 }} />
          <Tooltip formatter={(v, name) => [fmt$(v), name]} itemSorter={(i) => -i.value} />
          {OTHER_DISTRICTS.map((k) => (
            <Line key={k} type="monotone" dataKey={k} stroke={GREY} strokeWidth={1.5} dot={{ r: 2 }} isAnimationActive={false}>
              <LabelList dataKey={k} content={({ x, y, index }) =>
                index === districts.length - 1 ? (
                  <text x={x + 6} y={y + 4} fontSize={10} fill="#6B7480">{k}</text>
                ) : null
              } />
            </Line>
          ))}
          <Line type="monotone" dataKey="PADD" stroke={RUST} strokeWidth={3} dot={{ r: 4 }} isAnimationActive={false}>
            <LabelList dataKey="PADD" position="bottom" formatter={fmt$} style={{ fontSize: 11, fill: RUST, fontWeight: 700 }} />
          </Line>
        </LineChart>
      </ResponsiveContainer>
      <figcaption>
        Figure 2.3c. Per capita personal income across Nebraska’s seven economic
        development districts. District aggregates are population-weighted:
        total personal income divided by total population across member
        counties. The Panhandle ranks lowest in every year shown. Source: U.S.
        Bureau of Economic Analysis, Regional Economic Accounts (CAINC1).
      </figcaption>
    </figure>
  );
}
